import { useFlag } from '@calendly/feature-check-react'
import { useCallback, useEffect, useMemo } from 'react'
import { useDispatch } from 'react-redux'

import { setGetParams } from 'actions/core/navigation'
import {
  AllSchedulingMethods,
  MEETINGS_PRESET_IDS,
  MEETINGS_PRESET_PARAM,
  Periods,
  UPCOMING_PRESET_PARAM,
  UPCOMING_PRESET_OFF,
} from 'constants/meetings'
import { hasActiveNonSearchFilters } from 'features/meetings/utils'
import { useFilters } from 'hooks/meetings/filters'
import { useQueryParams } from 'hooks/use_query_params'

import { setOpenMeetingDate, setScrollAnchorDate, setViewDateLabelPinned } from '../meetings_page_slice'

/** Query param used to persist an explicit "untoggled" state across query changes. */
export { UPCOMING_PRESET_PARAM }

const PRESET_OFF = UPCOMING_PRESET_OFF

/** Default chip when landing on a clean meetings view with no `meetings_preset` param. */
const DEFAULT_PRESET_ID = MEETINGS_PRESET_IDS.TODAY

const PRESET_PARAM_IDS = new Set([
  MEETINGS_PRESET_IDS.UPCOMING,
  MEETINGS_PRESET_IDS.TODAY,
  MEETINGS_PRESET_IDS.THIS_WEEK,
  MEETINGS_PRESET_IDS.LAST_WEEK,
])

const PERIOD_BY_PRESET_ID = Object.freeze({
  [MEETINGS_PRESET_IDS.UPCOMING]: Periods.UPCOMING,
  [MEETINGS_PRESET_IDS.TODAY]: Periods.TODAY,
  [MEETINGS_PRESET_IDS.THIS_WEEK]: Periods.THIS_WEEK,
  [MEETINGS_PRESET_IDS.LAST_WEEK]: Periods.LAST_WEEK,
})

const SINGLE_QUERY_PRESET_PERIODS = new Set([
  Periods.TODAY,
  Periods.THIS_WEEK,
  Periods.LAST_WEEK,
])

/**
 * Preset chips only represent a clean default view. The filter bar keeps `period=upcoming`;
 * presets are stored in `meetings_preset` and applied only to list/export queries.
 *
 * Search is additive: it does not leave this view. Filter-modal filters still do.
 */
const isCleanPresetView = (params) => {
  if (params.period !== Periods.UPCOMING) return false
  if (hasActiveNonSearchFilters(params)) return false

  const schedulingMethods = params.scheduling_methods || []
  const isSchedulingDefault = schedulingMethods.length === 0
    || schedulingMethods.length >= AllSchedulingMethods.length

  return (
    isSchedulingDefault
    && !params.query
    && !params.start_date
    && !params.end_date
  )
}

const withPreservedSearch = (params, searchKeyword) => (
  searchKeyword ? { ...params, search_keyword: searchKeyword } : params
)

const resolveActivePresetId = (queryParams, explicitlyOff) => {
  if (explicitlyOff) return null

  const presetParam = queryParams[MEETINGS_PRESET_PARAM]
  if (presetParam && PRESET_PARAM_IDS.has(presetParam)) {
    return presetParam
  }

  return DEFAULT_PRESET_ID
}

/**
 * Drives the meetings page preset chips (behind `sa_default_preset_toggle`).
 *
 * Presets are mutually exclusive: selecting one turns the others off. Clicking the active chip
 * again untoggles it and restores the legacy meetings page (past + future split, no forward-only).
 *
 * State is derived from URL filter params plus an explicit `upcoming_preset=0` off marker.
 * Missing `meetings_preset` on a clean view defaults to Today (and is written into the URL).
 */
export const useUpcomingPreset = () => {
  const presetFlagOn = useFlag('sa_default_preset_toggle', true)
  const queryParams = useQueryParams()
  const dispatch = useDispatch()
  const { getFilterParams, applyFilterParams } = useFilters()

  const filterParams = getFilterParams()

  const explicitlyOff = queryParams[UPCOMING_PRESET_PARAM] === PRESET_OFF
  const cleanPresetView = isCleanPresetView(filterParams)

  const activePreset = useMemo(() => {
    if (!presetFlagOn || !cleanPresetView) return null
    return resolveActivePresetId(queryParams, explicitlyOff)
  }, [presetFlagOn, cleanPresetView, queryParams, explicitlyOff])

  // Persist the default Today selection when landing without an explicit meetings_preset param.
  useEffect(() => {
    if (!presetFlagOn || !cleanPresetView || explicitlyOff) return
    if (queryParams[MEETINGS_PRESET_PARAM]) return

    dispatch(setGetParams({ [MEETINGS_PRESET_PARAM]: DEFAULT_PRESET_ID }))
  }, [presetFlagOn, cleanPresetView, explicitlyOff, queryParams, dispatch])

  const presetActive = activePreset === MEETINGS_PRESET_IDS.UPCOMING
  const presetPeriod = activePreset && SINGLE_QUERY_PRESET_PERIODS.has(PERIOD_BY_PRESET_ID[activePreset])
    ? PERIOD_BY_PRESET_ID[activePreset]
    : null

  const turnOffPreset = useCallback(() => {
    if (!presetFlagOn) return
    dispatch(setOpenMeetingDate(null))
    dispatch(setScrollAnchorDate(null))
    dispatch(setViewDateLabelPinned(false))
    dispatch(applyFilterParams(withPreservedSearch({
      period: Periods.UPCOMING,
      [UPCOMING_PRESET_PARAM]: PRESET_OFF,
    }, filterParams.search_keyword)))
  }, [presetFlagOn, dispatch, applyFilterParams, filterParams.search_keyword])

  const selectPreset = useCallback((presetId) => {
    if (!presetFlagOn) return
    if (!PRESET_PARAM_IDS.has(presetId)) return

    dispatch(setOpenMeetingDate(null))
    dispatch(setScrollAnchorDate(null))
    dispatch(setViewDateLabelPinned(false))

    dispatch(applyFilterParams(withPreservedSearch({
      period: Periods.UPCOMING,
      [MEETINGS_PRESET_PARAM]: presetId,
    }, filterParams.search_keyword)))
  }, [presetFlagOn, dispatch, applyFilterParams, filterParams.search_keyword])

  const togglePreset = useCallback((presetId = DEFAULT_PRESET_ID) => {
    if (!presetFlagOn) return
    if (activePreset === presetId) {
      turnOffPreset()
    } else {
      selectPreset(presetId)
    }
  }, [presetFlagOn, activePreset, turnOffPreset, selectPreset])

  return {
    activePreset,
    presetActive,
    presetFlagOn,
    presetPeriod,
    selectPreset,
    togglePreset,
    turnOffPreset,
  }
}
